import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { safeRedirectPath } from '../lib/safeRedirect';
import { Container, Loader, Text, Stack, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import Layout from '../components/Layout/Layout';

/**
 * AuthCallback Component
 * Handles OAuth redirect and establishes session before navigating to profile
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log('🔄 AuthCallback: Processing OAuth callback...');
        console.log('🔍 Current URL:', window.location.href);
        console.log('🔍 Search params:', location.search);
        console.log('🔍 Hash:', location.hash);

        // Check if we have hash parameters (Supabase uses hash for OAuth)
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const searchParams = new URLSearchParams(location.search);

        // Get tokens first (needed for checks below)
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');

        // Check for password reset callback
        const type = hashParams.get('type') || searchParams.get('type');
        
        // Password reset comes with type=recovery in hash
        if (type === 'recovery') {
          console.log('🔑 Password reset/recovery detected, redirecting with params...');
          console.log('Hash:', location.hash);
          console.log('Search:', location.search);
          // Preserve both hash and search params when redirecting
          navigate(`/reset-password${location.search}${location.hash}`, { replace: true });
          return;
        }
        const errorParam = hashParams.get('error') || searchParams.get('error');
        const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');

        console.log('🔍 Access token present:', !!accessToken);
        console.log('🔍 Refresh token present:', !!refreshToken);

        if (errorParam) {
          console.error('❌ OAuth error:', errorParam, errorDescription);
          setError(errorDescription || errorParam);
          setIsProcessing(false);
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        // Let Supabase handle the session from URL automatically
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('❌ Session error:', sessionError);
          setError(sessionError.message);
          setIsProcessing(false);
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        if (session?.user) {
          console.log('✅ Session established for:', session.user.email);

          // Duplicate-account guard: if this OAuth sign-in just created a NEW account
          // for an email that already had a prior account (via password or a different
          // provider), sign the user out and send them back with a clear pointer to
          // their original sign-in method. Prevents the "silent duplicate" scenario.
          try {
            const conflictRes = await fetch('/api/check-email-conflict', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: session.user.email })
            });
            if (conflictRes.ok) {
              const conflict = await conflictRes.json();
              const isBrandNew = session.user.created_at &&
                (Date.now() - new Date(session.user.created_at).getTime()) < 60_000;
              if (conflict.exists && (conflict.accountCount || 0) > 1 && isBrandNew) {
                console.warn('⚠️ Duplicate account detected — signing out and blocking');
                await supabase.auth.signOut();
                // Determine the original provider (all providers except the one we just used).
                const currentProvider = session.user.app_metadata?.provider;
                const otherProviders = (conflict.providers || []).filter((p) => p !== currentProvider);
                const providerHint = otherProviders.length > 0
                  ? otherProviders.map((p) => p === 'email' ? 'email and password' : p === 'google' ? 'Google' : p).join(' or ')
                  : 'your original method';
                setError(`An account with this email already exists. Please sign in with ${providerHint} instead.`);
                setIsProcessing(false);
                setTimeout(() => navigate('/login', { replace: true }), 4000);
                return;
              }
            }
          } catch (checkErr) {
            console.warn('Post-OAuth duplicate check failed, continuing:', checkErr);
          }

          // Check for stored redirect path from OAuth login
          const redirectPath = safeRedirectPath(localStorage.getItem('oauth_redirect'));
          localStorage.removeItem('oauth_redirect');

          console.log('✅ Redirecting to:', redirectPath);
          setIsProcessing(false);
          navigate(redirectPath, { replace: true });
        } else {
          console.log('⚠️ No session found, checking for tokens in URL...');

          // If no session but we have tokens, try to set the session manually
          if (accessToken && refreshToken) {
            console.log('🔑 Setting session from URL tokens...');
            const { data, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (setSessionError) {
              console.error('❌ Error setting session:', setSessionError);
              setError(setSessionError.message);
              setIsProcessing(false);
              setTimeout(() => navigate('/login', { replace: true }), 3000);
              return;
            }

            if (data?.session) {
              console.log('✅ Session set successfully for:', data.session.user.email);

              // Check for stored redirect path from OAuth login
              const redirectPath = safeRedirectPath(localStorage.getItem('oauth_redirect'));
              localStorage.removeItem('oauth_redirect');

              setIsProcessing(false);
              navigate(redirectPath, { replace: true });
            }
          } else {
            console.log('❌ No user session or tokens found, redirecting to login...');
            setError('Authentication failed. Please try again.');
            setIsProcessing(false);
            setTimeout(() => navigate('/login', { replace: true }), 2000);
          }
        }
      } catch (err) {
        console.error('❌ Unexpected error in OAuth callback:', err);
        setError(err.message || 'An unexpected error occurred');
        setIsProcessing(false);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    handleOAuthCallback();
  }, [navigate, location]);

  return (
    <Layout
      title="Signing In | Prix du Canada Ballet"
      description="Completing your sign in..."
    >
      <section className="section" style={{ background: 'var(--light-gray)', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container size="sm">
          <Stack align="center" spacing="xl">
            {error ? (
              <>
                <Alert icon={<IconAlertCircle size="1rem" />} color="red" title="Authentication Error">
                  {error}
                  <br />
                  <Text size="sm" mt="xs">Redirecting you back to login...</Text>
                </Alert>
              </>
            ) : isProcessing ? (
              <>
                <Loader size="xl" style={{ color: 'var(--primary-gold)' }} />
                <Text size="lg" weight={600} style={{ color: 'var(--charcoal)' }}>
                  Completing your sign in...
                </Text>
                <Text size="sm" color="dimmed">
                  Please wait while we set up your session
                </Text>
              </>
            ) : (
              <>
                <Loader size="xl" style={{ color: 'var(--primary-gold)' }} />
                <Text size="lg" weight={600} style={{ color: 'var(--charcoal)' }}>
                  Success! Redirecting...
                </Text>
              </>
            )}
          </Stack>
        </Container>
      </section>
    </Layout>
  );
};

export default AuthCallback;
